<template>
  <div class="page-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Staff Members"
      subtitle="Manage society staff and their roles"
      add-label="Add"
      :show-edit="true"
      :show-delete="true"
      @add="openStaffAdd"
      @edit="openStaffEdit($event)"
      @delete="confirmDeleteDialog($event.id)"
      :columns="columns"
      :items="staffs"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
      @update:options="onOptionsUpdate"
    >
      <!-- Custom Cells -->
      <template #col-picture_url="{ item }">
        <div class="profile-cell">
          <div class="p-avatar-wrap">
            <img
              v-if="item.picture_url"
              :src="API_BASE_URL + '/uploads/' + item.picture_url"
              class="p-avatar-img"
              style="cursor: pointer"
              @click.stop="openPhoto(API_BASE_URL + '/uploads/' + item.picture_url, item.name)"
            />
            <div
              v-else
              class="p-avatar-placeholder"
              :style="{ background: primaryColor + '20', color: primaryColor }"
            >
              {{ item.name ? item.name.charAt(0).toUpperCase() : "S" }}
            </div>
          </div>
          <div class="p-info">
            <span class="p-name">{{ item.name }}</span>
            <!-- <span class="p-id " v-if="item.id">ID: #ST{{ item.id }}</span> -->
          </div>
        </div>
      </template>

      <template #col-mobile_no="{ item }">
        <div class="mobile-cell">
          <v-icon size="14" color="#94A3B8" class="mr-1"
            >mdi-phone-outline</v-icon
          >
          <span
            >{{
              item.country_code
                ? (item.country_code.toString().startsWith("+")
                    ? item.country_code
                    : "+" + item.country_code) + " "
                : ""
            }}{{ item.mobile_no }}</span
          >
        </div>
      </template>

      <template #col-designation="{ item }">
        <span class="deg-pill">{{ item.designation || "Staff" }}</span>
      </template>
    </PremiumTable>

    <!-- ── Add / Edit Modal ────────────────── -->
    <PremiumModal
      v-model="staffDialog"
      :title="(staff.id ? 'Edit' : 'Add') + ' Staff Member'"
      :loading="saveLoading"
      submit-label="Submit"
      max-width="500"
      @submit="staff.id ? updateStaff() : saveStaff()"
      @close="closeStaffDialog"
    >
      <!-- Avatar uploader -->
      <div class="d-flex justify-center mb-6">
        <input
          type="file"
          ref="staff_pic"
          @change="changeFile($event)"
          accept="image/*"
          hidden
        />
        <div class="avatar-uploader-container">
          <v-avatar size="100" class="elevation-3 staff-avatar">
            <img
              v-if="previewImage || staff.picture_url"
              :src="
                previewImage || API_BASE_URL + '/uploads/' + staff.picture_url
              "
              style="object-fit: contain"
            />
            <span
              v-else
              class="text-h3 font-weight-bold"
              :style="`color:${primaryColor}`"
            >
              {{ staff.name ? staff.name.charAt(0).toUpperCase() : "S" }}
            </span>
          </v-avatar>
          <v-btn
            fab
            small
            depressed
            @click="openFileExplorer"
            class="avatar-edit-btn"
             color="var(--brand)"
          >
            <v-icon small color="white">mdi-camera</v-icon>
          </v-btn>
        </div>
      </div>

      <v-row dense>
        <!-- Name -->
        <v-col cols="12">
          <PremiumInput
            v-model="staff.name"
            label="Full Name *"
            placeholder="Enter staff member's name"
            icon="mdi-account-outline"
            v-validate="'required'"
            name="staff_name"
            data-vv-as="Name"
            :error="errors.first('staff_name') || serverSideErrors.name"
          />
        </v-col>

        <!-- Contact No (country code + mobile inline) -->
        <v-col cols="12">
          <div class="premium-field-label mb-2">Contact No *</div>
          <div class="phone-row">
            <div class="country-code-wrap">
              <vue-country-code
                @onSelect="onSelect"
                class="custom-country-code-select-full"
                :defaultCountry="staff.countryIso || 'IN'"
              />
            </div>
            <div class="mobile-input-wrap">
              <PremiumInput
                v-model="staff.mobile_no"
                label=""
                placeholder="Mobile number"
                icon="mdi-phone-outline"
                v-validate="'required|numeric|max:10|min:10'"
                name="staff_mobile"
                data-vv-as="Contact No"
                :error="
                  errors.first('staff_mobile') || serverSideErrors.mobile_no
                "
              />
            </div>
          </div>
        </v-col>

        <!-- Designation -->
        <v-col cols="12">
          <PremiumInput
            v-model="staff.designation"
            label="Designation *"
            placeholder="e.g. Plumber, Electrician"
            icon="mdi-briefcase-outline"
            v-validate="'required'"
            name="staff_designation"
            data-vv-as="Designation"
            :error="
              errors.first('staff_designation') || serverSideErrors.designation
            "
          />
        </v-col>
      </v-row>
    </PremiumModal>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Staff"
      message="Are you sure you want to remove this staff member? Their access and history will be preserved as archival data."
      confirm-text="Yes, Remove Staff"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteStaff"
    />

    <!-- ── Media Viewer ────────────────────── -->
    <MediaViewer
      v-model="mediaViewer.open"
      :items="mediaViewer.items"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapMutations, mapState } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumModal from "../../common/PremiumModal.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import MediaViewer from "../../common/MediaViewer.vue";
import VueCountryCode from "vue-country-code";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "StaffList",
  mixins: [filterMixin],
  components: {
    PremiumTable,
    PremiumModal,
    PremiumInput,
    ConfirmModal,
    VueCountryCode,
    MediaViewer,
  },

  data() {
    return {
      search: "",
      staffs: [],
      totalRecords: 0,
      loading: false,
      saveLoading: false,
      deleteLoading: false,
      deleteDialog: false,
      staffDialog: false,
      delete_id: "",
      page: 1,
      itemsPerPage: 10,
      options: {
        sortBy: ["created_at"],
        sortDesc: [true],
      },
      user: {},
      // Staff form
      staff: {
        id: null,
        name: "",
        mobile_no: "",
        country_code: "+91",
        countryIso: "IN",
        designation: "",
        picture_url: null,
      },
      profile_pic: null,
      previewImage: null,
      serverSideErrors: {},
      mediaViewer: { open: false, items: [] },
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor"]),
    API_BASE_URL: () => process.env.VUE_APP_API_URL,

    columns() {
      return [
        {
          key: "picture_url",
          label: "Member",
          width: "280px",
        },
        {
          key: "mobile_no",
          label: this.$t("staff.headers.mobile_no") || "Contact",
          width: "200px",
        },
        {
          key: "designation",
          label: this.$t("staff.headers.designation") || "Designation",
          minWidth: "200px",
        },
      ];
    },
  },

  watch: {
    page() {
      this.getStaffs();
    },
    itemsPerPage() {
      this.getStaffs();
    },
    search() {
      this.page = 1;
      this.getStaffs();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSelect(event) {
      this.staff.country_code = "+" + event.dialCode;
      this.staff.countryIso = event.iso2;
    },

    onSearch(val) {
      this.search = val;
    },

    onOptionsUpdate(opt) {
      this.options.sortBy = opt.sortBy || ["created_at"];
      this.options.sortDesc = opt.sortDesc || [true];
      this.getStaffs();
    },

    openFileExplorer() {
      this.$refs.staff_pic.click();
    },

    changeFile(e) {
      this.profile_pic = e.target.files[0];
      const input = this.$refs.staff_pic;
      const file = input.files;
      if (file && file[0]) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          this.previewImage = ev.target.result;
        };
        reader.readAsDataURL(file[0]);
      }
    },

    openStaffAdd() {
      this.staff = {
        id: null,
        name: "",
        mobile_no: "",
        country_code: "+91",
        countryIso: "IN",
        designation: "",
        picture_url: null,
      };
      this.profile_pic = null;
      this.previewImage = null;
      this.$validator.reset();
      this.serverSideErrors = {};
      this.staffDialog = true;
    },

    openStaffEdit(item) {
      this.staff = {
        id: item.id,
        name: item.name || "",
        mobile_no: item.mobile_no || "",
        country_code: item.country_code || "+91",
        countryIso: item.countryIso || "IN",
        designation: item.designation || "",
        picture_url: item.picture_url || null,
      };
      this.profile_pic = null;
      this.previewImage = null;
      this.$validator.reset();
      this.serverSideErrors = {};
      this.staffDialog = true;
    },

    saveStaff() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;
        this.saveLoading = true;
        const formData = new FormData();
        formData.append("name", this.staff.name);
        formData.append("mobile_no", this.staff.mobile_no);
        formData.append("country_code", this.staff.country_code);
        formData.append("designation", this.staff.designation);
        if (this.profile_pic) formData.append("picture", this.profile_pic);

        this.$axios
          .post("/societyadmin/staffs/", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((response) => {
            this.saveLoading = false;
            const data = response.data;
            if (data.status) {
              this.setAlert({
                showAlert: true,
                message: data.message,
                variant: "green accent-4",
                timeout: 2000,
              });
              this.getStaffs();
              this.closeStaffDialog();
            } else {
              this.handleErrors(data);
            }
          })
          .catch(() => {
            this.saveLoading = false;
          });
      });
    },

    updateStaff() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;
        this.saveLoading = true;
        const formData = new FormData();
        formData.append("name", this.staff.name);
        formData.append("mobile_no", this.staff.mobile_no);
        formData.append("country_code", this.staff.country_code);
        formData.append("designation", this.staff.designation);
        if (this.profile_pic) formData.append("picture", this.profile_pic);

        this.$axios
          .put("/societyadmin/staffs/" + this.staff.id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((response) => {
            this.saveLoading = false;
            const data = response.data;
            if (data.status) {
              this.setAlert({
                showAlert: true,
                message: data.message,
                variant: "green accent-4",
                timeout: 2000,
              });
              this.getStaffs();
              this.closeStaffDialog();
            } else {
              this.handleErrors(data);
            }
          })
          .catch(() => {
            this.saveLoading = false;
          });
      });
    },

    closeStaffDialog() {
      this.staffDialog = false;
      this.staff = {
        id: null,
        name: "",
        mobile_no: "",
        country_code: "+91",
        countryIso: "IN",
        designation: "",
        picture_url: null,
      };
      this.profile_pic = null;
      this.previewImage = null;
      this.serverSideErrors = {};
      this.$validator.reset();
    },

    getStaffs() {
      this.loading = true;
      const sortBy = this.options.sortBy[0] || "";
      const sortDesc = this.options.sortDesc[0] || "";

      const params = [
        `society_id=${this.user.id}`,
        `page=${this.page}`,
        `perPage=${this.itemsPerPage}`,
        `sortBy=${sortBy}`,
        `sortDesc=${sortDesc}`,
        `search=${this.search || ""}`,
      ].join("&");

      this.$axios
        .get("societyadmin/staffs?" + params)
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.staffs = data.staffs;
            this.totalRecords = data.total;
          } else {
            this.showError(data.message);
            if (data.type === "invalid") {
              localStorage.removeItem("user");
              this.$router.push("/societyadmin/login");
            }
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    confirmDeleteDialog(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteStaff() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/staffs/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          const data = response.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getStaffs();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },

    handleErrors(data) {
      if (data.errors) {
        this.serverSideErrors = {};
        data.errors.forEach((e) => {
          this.serverSideErrors[e.field] = e.message;
        });
      } else {
        this.showError(data.message);
      }
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },

    openPhoto(url, name) {
      this.mediaViewer.items = [{ url, type: "image", name: name || "Photo" }];
      this.mediaViewer.open = true;
    },

    debounceSearch: debounce(function () {
      this.page = 1;
      this.getStaffs();
    }, 500),
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};

    // Initialize persistent filters
    this.initFilters("staff");

    this.getStaffs();
    if (this.$route.query.add === "1") {
      this.$nextTick(() => this.openStaffAdd());
    }
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Table Cells ── */
.profile-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}
.p-avatar-wrap {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--surface2);
  border: 1px solid var(--border);
}
.p-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.p-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
}
.p-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.p-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx);
}
.p-id {
  font-size: 10px;
  font-weight: 700;
  color: var(--tx4, #94a3b8);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.premium-field-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--tx3, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.mobile-cell {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2);
}
.deg-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  background: var(--surface2, #f1f5f9);
  color: var(--tx2, #475569);
  font-size: 11px;
  font-weight: 700;
  border: 1px solid var(--border);
}

/* ── Phone Row (country code + mobile inline) ── */
.phone-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
.country-code-wrap {
  flex-shrink: 0;
  width: 90px;
}
.mobile-input-wrap {
  flex: 1;
  min-width: 0;
}
.custom-country-code-select-full {
  width: 100%;
  height: 40px;
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1)) !important;
  background-color: var(--surface2) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}
.custom-country-code-select-full:hover {
  border-color: var(--brand) !important;
}
.theme--dark .custom-country-code-select-full {
  background-color: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* ── Avatar Uploader ── */
.avatar-uploader-container {
  position: relative;
  display: inline-block;
}
.staff-avatar {
  background: var(--surface2) !important;
  border: 2px solid var(--border, rgba(0, 0, 0, 0.08)) !important;
}
.avatar-edit-btn {
  position: absolute !important;
  bottom: 2px !important;
  right: 2px !important;
  width: 38px !important;
  height: 38px !important;
  border: 2px solid white !important;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.12) !important;
  transition: all 0.2s ease !important;
  background-color: rgb(229, 229, 229) !important;
}

.avatar-edit-btn .v-icon {
  color: #000 !important;
}

.theme--dark .avatar-edit-btn .v-icon {
  color: #fff !important;
}

.avatar-edit-btn:hover {
  transform: scale(1.1);
}

.theme--dark .avatar-edit-btn {
  border-color: #111827 !important;
}

.theme--dark .avatar-edit-btn,
.theme--dark .avatar-border {
  border-color: var(--surface2, #1E1E1E) !important;
}
.theme--dark .avatar-edit-btn {
  background-color: black !important;
}

</style>
